/**
 * HomePage — Marketing landing page for TableTryb.
 * Public-facing, no authentication required.
 * PLACEHOLDER: Update copy, images, and branding when finalized.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { BRAND } from '../../config/branding';
import {
  Camera, Globe, ThumbsUp, ShoppingCart, Users, Smartphone,
  ChefHat, ArrowRight, Scan, Link as LinkIcon,
  LockKeyhole
} from 'lucide-react';

const HomePage: React.FC = () => {
  return (
    <div className="home-page">

      {/* ============================================================ */}
      {/* HERO SECTION */}
      {/* ============================================================ */}
      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-title">
            Your family's meals,<br />
            <span className="hero-highlight">planned together.</span>
          </h1>
          <p className="hero-subtitle">
            {BRAND.description}
          </p>
          <div className="hero-cta-group">
            <Link to="/signup" className="btn btn-primary btn-lg">
              Start Free Trial
              <ArrowRight size={20} />
            </Link>
            <Link to="/pricing" className="btn btn-secondary btn-lg">
              See Pricing
            </Link>
          </div>
          <p className="hero-note">14-day free trial. No credit card required.</p>
        </div>
        <div className="hero-visual">
          <img
            src="/assets/hero-screenshot.jpeg"
            alt="TableTryb meal planning dashboard"
            className="hero-image"
          />
        </div>
      </section>

      {/* ============================================================ */}
      {/* HOW IT WORKS — 4 Steps */}
      {/* ============================================================ */}
      <section className="how-it-works">
        <h2 className="section-title">How it works</h2>
        <p className="section-subtitle">
          From recipe to grocery cart in four simple steps.
        </p>

        <div className="steps-grid">
          <div className="step-card">
            <div className="step-number" style={{ background: 'var(--color-primary)' }}>1</div>
            <div className="step-icon-group" style={{ color: 'var(--color-primary)' }}>
              <Users size={28} />
            </div>
            <h3>Build Your Tribe</h3>
            <p>
              Create your household and invite family members, roommates, or anyone
              you share meals with. One subscription covers everyone — adding members is always free.
            </p>
          </div>

          <div className="step-card">
            <div className="step-number" style={{ background: 'var(--color-secondary-light)' }}>2</div>
            <div className="step-icon-group" style={{ color: 'var(--color-secondary-light)' }}>
              <Camera size={28} />
              <LinkIcon size={28} />
              <Scan size={28} />
            </div>
            <h3>Import Your Recipes</h3>
            <p>
              Snap a photo of a recipe card, paste a URL from any food blog,
              or type it in manually. We extract every ingredient and step — no typing required.
            </p>
          </div>

          <div className="step-card">
            <div className="step-number" style={{ background: 'var(--color-secondary)' }}>3</div>
            <div className="step-icon-group" style={{ color: 'var(--color-secondary)' }}>
              <ThumbsUp size={28} />
            </div>
            <h3>Vote Together</h3>
            <p>
              Every family member picks their favorites for the week.
              No more "what's for dinner?" debates — let the votes speak for themselves.
            </p>
          </div>

          <div className="step-card">
            <div className="step-number" style={{ background: 'var(--color-accent)' }}>4</div>
            <div className="step-icon-group" style={{ color: 'var(--color-accent)' }}>
              <ShoppingCart size={28} />
            </div>
            <h3>Shop Smarter</h3>
            <p>
              One-tap grocery list grouped by store aisle, with pantry staples separated.
              Quick search links at your favorite stores for easy online ordering or in-store shopping.
            </p>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* FEATURES GRID */}
      {/* ============================================================ */}
      <section className="features">
        <h2 className="section-title">Everything your family needs</h2>

        <div className="features-grid">
          {[
            {
              icon: Camera,
              title: 'Smart Recipe Import',
              desc: 'Snap a photo of a cookbook page, paste a blog URL, or type it in. Ingredients and instructions are extracted instantly — no manual entry needed.',
              color: 'var(--color-secondary)',
            },
            {
              icon: ThumbsUp,
              title: 'Family Voting',
              desc: 'Every member votes thumbs up or down. See results in real-time and make dinner decisions together.',
              color: 'var(--color-primary)',
            },
            {
              icon: ShoppingCart,
              title: 'Smart Grocery Lists',
              desc: 'Auto-generated from your finalized meals, organized by store aisle, with pantry staples separated. Admins control who can finalize meals and manage the list.',
              color: 'var(--color-secondary-light)',
            },
            {
              icon: Globe,
              title: 'Store Integrations',
              desc: 'Quicksearch items at most grocery stores including Kroger, H-E-B, Walmart, Target, and more.',
              color: 'var(--color-accent)',
            },
            {
              icon: Smartphone,
              title: 'Works on Any Device',
              desc: 'Plan, vote, or generate your list on your laptop, phone, or tablet.',
              color: 'var(--color-secondary)',
            },
            {
              icon: LockKeyhole,
              title: 'Your Data Stays Yours',
              desc: 'We don\'t sell your recipes, grocery lists, or eating habits to anyone. Ever. Your data is never used to train AI models. Our subscription model means we work for you — not advertisers.',
              color: 'var(--color-primary)',
            },
          ].map((feature) => (
            <div key={feature.title} className="feature-card">
              <feature.icon size={32} style={{ color: feature.color }} />
              <h3>{feature.title}</h3>
              <p>{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============================================================ */}
      {/* PRICING CTA */}
      {/* ============================================================ */}
      <section className="pricing-cta">
        <h2 className="section-title">One simple plan. No surprises.</h2>
        <p className="section-subtitle">
          Everything included. 14-day free trial. Cancel anytime.
        </p>
        <Link to="/pricing" className="btn btn-primary btn-lg">
          See Pricing
          <ArrowRight size={20} />
        </Link>
      </section>

      {/* ============================================================ */}
      {/* FINAL CTA */}
      {/* ============================================================ */}
      <section className="final-cta">
        <h2>Ready to end the "what's for dinner?" debate?</h2>
        <p>Join families who plan meals together with {BRAND.name}.</p>
        <Link to="/signup" className="btn btn-primary btn-lg">
          Start Your Free Trial
          <ArrowRight size={20} />
        </Link>
      </section>
    </div>
  );
};

export default HomePage;
